// src/lib/device-cache.ts
//
// WHY: iOS drops BuildSynx from memory the moment the user switches apps, so every
// return trip was a full cold boot — empty Tasks, empty To-Dos, blank metrics —
// until fresh data came back over the network. This module persists a SMALL
// allow-list of read-mostly React Query data (PERSISTED_QUERY_ROOTS below) to
// IndexedDB so those screens repaint instantly from last-seen data, then refetch
// quietly in the background. Kept 24h, capped at 2MB, cleared on sign-out / owner
// change, and busted automatically on every deploy. Money data (invoices, bills,
// ledgers, …) is deliberately excluded — it stays memory-only, never written to disk.

import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import type { Persister } from "@tanstack/query-persist-client-core";
import { get as idbGet, set as idbSet, del as idbDel, keys as idbKeys } from "idb-keyval";

/** Prefix for every device-cache entry in the KeyValueStore. One entry per owner (userId, or "anon"). */
export const DEVICE_CACHE_KEY_PREFIX = "buildsynx-rq-cache:";

/** localStorage key that remembers which user the on-device cache currently belongs to. */
export const DEVICE_CACHE_OWNER_KEY = "buildsynx:device-cache-owner";

/** Max age of a persisted cache before React Query discards it outright. */
export const DEVICE_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Hard byte cap on the serialized cache payload written to IndexedDB. */
export const DEVICE_CACHE_MAX_BYTES = 2_000_000;

/**
 * Cache-buster string. Each deploy gets a new Vercel git SHA, so a device cache
 * written by a previous deploy is discarded on restore instead of being hydrated
 * against code that may no longer understand its shape.
 */
export const DEVICE_CACHE_BUSTER: string = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? "dev";

/**
 * Allow-list of top-level query-key roots that are safe to persist to disk. Only
 * read-mostly, non-monetary lists belong here. Verified against the actual
 * queryKey literals in each source file:
 *
 *   - "tasks"              src/context/TaskContext.tsx          — useInfiniteQuery queryKey ['tasks', {...}]
 *   - "tasks-priority"     src/context/TaskContext.tsx          — priority useQuery queryKey ['tasks-priority', {...}]
 *   - "task-metrics"       src/app/(dashboard)/tasks/page.tsx   — useQuery queryKey ['task-metrics']
 *   - "todos"              src/components/tasks/ToDoTab.tsx     — useQuery queryKey ["todos", {...}]
 *   - "contacts"           src/hooks/useContacts.ts             — CONTACTS_QUERY_KEY = ["contacts", "selector"]
 *   - "services"           src/hooks/useServices.ts             — SERVICES_QUERY_KEY = ["services"] /
 *                                                                  SERVICES_WITH_ARCHIVED_QUERY_KEY = ["services", "withArchived"]
 *   - "expense-categories" src/app/(dashboard)/finance/page.tsx — useQuery queryKey ["expense-categories"]
 *   - "projects-list"      src/app/(dashboard)/finance/page.tsx — useQuery queryKey ["projects-list"]
 *   - "project-rail"       src/hooks/useProjectRail.ts         — PROJECT_RAIL_QUERY_KEY = ["project-rail"]
 *                                                                  (starred + active project names/statuses + last tab)
 *
 * Nothing else gets persisted — money data (bills, invoices, ledgers, …) stays
 * memory-only by design.
 */
export const PERSISTED_QUERY_ROOTS: ReadonlySet<string> = new Set([
    "tasks",
    "tasks-priority",
    "task-metrics",
    "todos",
    "contacts",
    "services",
    "expense-categories",
    "projects-list",
    "project-rail",
]);

/**
 * Whether a query is eligible for on-device persistence: it must have
 * succeeded (never persist pending/error state) and its top-level queryKey
 * segment must be on the allow-list above.
 */
export function shouldPersistQuery(query: { queryKey: readonly unknown[]; state: { status: string } }): boolean {
    if (query.state.status !== "success") return false;
    const root = query.queryKey[0];
    if (typeof root !== "string") return false;
    return PERSISTED_QUERY_ROOTS.has(root);
}

/**
 * Pure function: shrinks a dehydrated React Query client to fit under
 * `maxBytes` by dropping the largest queries first (by serialized JSON
 * length). Never mutates `client` — returns the original reference untouched
 * when already under budget, otherwise a new object with a trimmed queries
 * array.
 */
export function capDehydratedSize<T extends { clientState: { queries: Array<unknown> } }>(client: T, maxBytes: number): T {
    if (JSON.stringify(client).length <= maxBytes) return client;

    let queries = client.clientState.queries.slice();

    const withQueries = (qs: Array<unknown>): T => ({
        ...client,
        clientState: {
            ...client.clientState,
            queries: qs,
        },
    });

    // Repeatedly drop whichever remaining query serializes to the most bytes,
    // re-measuring the real payload each time rather than estimating.
    while (queries.length > 0 && JSON.stringify(withQueries(queries)).length > maxBytes) {
        let largestIndex = 0;
        let largestSize = -1;
        for (let i = 0; i < queries.length; i++) {
            const size = JSON.stringify(queries[i]).length;
            if (size > largestSize) {
                largestSize = size;
                largestIndex = i;
            }
        }
        queries = queries.slice(0, largestIndex).concat(queries.slice(largestIndex + 1));
    }

    return withQueries(queries);
}

/** The localStorage subset device-cache needs — matches window.localStorage's shape. */
export type StorageLike = {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
};

function defaultLocalStorage(): StorageLike | null {
    try {
        if (typeof window === "undefined" || !window.localStorage) return null;
        return window.localStorage;
    } catch {
        return null;
    }
}

/** Reads which user the on-device cache currently belongs to. Guarded — never throws. */
export function getDeviceCacheOwner(storage?: StorageLike | null): string | null {
    try {
        const target = storage === undefined ? defaultLocalStorage() : storage;
        if (!target) return null;
        return target.getItem(DEVICE_CACHE_OWNER_KEY);
    } catch {
        return null;
    }
}

/** Stamps the on-device cache as belonging to `userId`. Guarded — never throws. */
export function setDeviceCacheOwner(userId: string, storage?: StorageLike | null): void {
    try {
        const target = storage === undefined ? defaultLocalStorage() : storage;
        if (!target) return;
        target.setItem(DEVICE_CACHE_OWNER_KEY, userId);
    } catch {
        // Best-effort only — losing the owner stamp costs one extra cold boot
        // on next sign-in, never a crash.
    }
}

/**
 * Minimal async key-value contract device-cache needs from its backing store.
 * idb-keyval's top-level get/set/del/keys functions already match this shape.
 */
export type KeyValueStore = {
    get(k: string): Promise<unknown>;
    set(k: string, v: unknown): Promise<void>;
    del(k: string): Promise<void>;
    keys(): Promise<string[]>;
};

function defaultKeyValueStore(): KeyValueStore {
    return {
        get: (k) => idbGet(k),
        set: (k, v) => idbSet(k, v),
        del: (k) => idbDel(k),
        keys: () => idbKeys<string>(),
    };
}

/**
 * Builds a React Query `Persister` backed by `opts.store` (IndexedDB via
 * idb-keyval by default). One persisted entry per device-cache owner, so
 * switching accounts on the same device never bleeds one tenant's cached
 * lists into another's. Every store access is guarded so a storage failure
 * (quota, private mode, disabled IndexedDB) degrades to "cache miss", never
 * a thrown error.
 *
 * The target key is resolved on EVERY read/write, not once at creation time.
 * `createAsyncStoragePersister` captures whatever `key` string it's given at
 * construction and reuses that same captured value for the lifetime of the
 * persister — so a persister built once at app boot (see QueryProvider) must
 * NOT bake in the owner at that moment, or it silently keeps writing under a
 * stale owner after a different account signs in on the same device. Instead
 * our `storage` adapter below ignores the `k` argument `createAsyncStoragePersister`
 * passes through and recomputes the real key via `keyFor()` on every call, so
 * the write always lands under whoever is the CURRENT owner.
 */
export function createDevicePersister(opts?: {
    store?: KeyValueStore;
    /** Static owner override — pins the key for the persister's lifetime. Wins over `resolveOwner` when provided (used by tests). */
    owner?: string | null;
    /** Resolves the current owner on every call. Defaults to reading the live device-cache owner stamp. Ignored when `owner` is provided. */
    resolveOwner?: () => string | null;
}): Persister {
    const store = opts?.store ?? defaultKeyValueStore();
    const resolveOwner = opts?.resolveOwner ?? (() => getDeviceCacheOwner());
    const keyFor = () => `${DEVICE_CACHE_KEY_PREFIX}${(opts?.owner !== undefined ? opts.owner : resolveOwner()) ?? "anon"}`;

    return createAsyncStoragePersister({
        key: keyFor(),
        throttleTime: 1000,
        storage: {
            getItem: async () => {
                try {
                    const value = await store.get(keyFor());
                    return typeof value === "string" ? value : null;
                } catch {
                    return null;
                }
            },
            setItem: async (_k: string, value: string) => {
                try {
                    await store.set(keyFor(), value);
                } catch {
                    // Best-effort write — a dropped cache write must never surface to the user.
                }
            },
            removeItem: async () => {
                try {
                    await store.del(keyFor());
                } catch {
                    // Best-effort removal.
                }
            },
        },
        serialize: (client) => JSON.stringify(capDehydratedSize(client, DEVICE_CACHE_MAX_BYTES)),
        deserialize: (cached: string) => JSON.parse(cached),
    });
}

/**
 * Wipes every persisted device-cache entry (all owners) and forgets the
 * current owner stamp. Called on sign-out and on owner mismatch. Never
 * throws — a failed cache wipe must never block sign-out.
 */
export async function clearDeviceCache(store?: KeyValueStore): Promise<void> {
    try {
        const target = store ?? defaultKeyValueStore();
        const allKeys = await target.keys();
        const staleKeys = allKeys.filter((k) => typeof k === "string" && k.startsWith(DEVICE_CACHE_KEY_PREFIX));
        await Promise.all(
            staleKeys.map(async (k) => {
                try {
                    await target.del(k);
                } catch {
                    // Best-effort — one stuck key must not block clearing the rest.
                }
            })
        );
    } catch {
        // Best-effort — never let a storage failure block sign-out.
    }

    try {
        defaultLocalStorage()?.removeItem(DEVICE_CACHE_OWNER_KEY);
    } catch {
        // Ignore — storage may be unavailable/blocked.
    }
}

/**
 * Run right after the on-device cache is restored: resume any paused
 * mutations, then mark every restored query stale so active screens refetch
 * immediately (stale-while-revalidate).
 *
 * BUG this closes: a restored query's `dataUpdatedAt` can still be younger
 * than `staleTime` (default 5 min), so React Query treats it as fresh and
 * skips the on-mount refetch — the screen keeps showing the on-device
 * snapshot forever instead of picking up what changed while the app was
 * gone. Never throws, so a failure here can't block rendering the restored
 * (stale) data.
 */
export async function onDeviceCacheRestored(client: {
    resumePausedMutations(): Promise<unknown>;
    invalidateQueries(): Promise<void>;
}): Promise<void> {
    await client.resumePausedMutations().catch(() => undefined);
    await client.invalidateQueries().catch(() => undefined);
}
